import { useEffect, useState } from 'react';

// How long anything in the app takes to leave — --mg-close-duration in App.scss, rounded up.
//
// The number has to be known in JS as well as CSS because nothing can animate out after it has been
// unmounted, and something has to decide when to pull it. Overshooting is harmless: by then the
// element is already invisible and deaf to the pointer. Rounded up rather than read back off the
// element, because an animationend listener would never fire for anyone browsing with reduced
// motion — the animations are shortened to nothing there, not switched off — and whatever was
// leaving would stay forever.
export const EXIT_MS = 200;

/**
 * Keeps something mounted for a beat after it is closed, so it has time to play its exit.
 *
 * Returns whether to render. While it is true and `open` is false, the thing is on its way out and
 * should be wearing whatever class plays that — see the `dismiss` mixins in styles/_animations.scss.
 *
 * Only the rendering waits. Everything else a caller keys off `open` still turns off the moment it
 * is closed, so something leaving stops answering the pointer, the keyboard and its own shortcuts
 * rather than staying live while it fades.
 *
 * Reopening inside that beat cancels the pull, which is what makes a double tap land on the thing
 * that is still there instead of rebuilding it — and because swapping the animation name restarts
 * it, the arrival plays again from the top.
 */
const useDelayedUnmount = (open, exitMs = EXIT_MS) => {
  const [mounted, setMounted] = useState(open);

  useEffect(() => {
    if (open) {
      setMounted(true);

      return undefined;
    }

    if (!mounted) return undefined;

    const timer = setTimeout(() => setMounted(false), exitMs);

    return () => clearTimeout(timer);
  }, [open, mounted, exitMs]);

  return mounted;
};

export default useDelayedUnmount;
